<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/users';
    import { computed, ref, watch } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { onMounted } from 'vue';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import { useUrlSearch } from '@/composables/useUrlSearch';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateUser from '@/components/users/CreateUser.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { ColumnFiltersState, SortingState } from '@tanstack/vue-table';
    import type { SharedData, BreadcrumbItem, User } from '@/types';

    const { hasPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.administration',
        },
        {
            title: 'users.header',
            href: route('v2.users.index'),
        },
    ];

    const totalRecords = ref(0);
    const pagination = ref({
        pageIndex: 0,
        pageSize: 20,
    });
    const columnFilters = ref<ColumnFiltersState>([]);

    const { extractAndCleanSearch } = useUrlSearch();
    const globalFilter = ref(extractAndCleanSearch());
    const sorting = ref<SortingState>([]);

    const initialQueryDone = ref(false);

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.USERS, selectedCompanyId],
        queryFn: getUsers,
    });

    watch(
        () => isFetching.value,
        (newVal, oldVal) => {
            if (!initialQueryDone.value && oldVal === true && newVal === false) {
                initialQueryDone.value = true;
            }
        },
    );

    async function getUsers() {
        const { data } = await axios.get(route('v2.users.table', selectedCompanyId.value), {
            params: {
                length: pagination.value.pageSize,
                start: pagination.value.pageIndex * pagination.value.pageSize,
                search: globalFilter.value || undefined,
                filters: buildFilterParams(columnFilters.value),
                orderBy: sorting.value[0]?.id,
                orderDir: sorting.value[0]?.desc ? 'desc' : 'asc',
            },
        });
        totalRecords.value = data.recordsTotal;
        return data.data;
    }

    const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
        pagination.value = newPagination;
        refetch();
    };

    const handleGlobalFilterChange = (value: string) => {
        if (!initialQueryDone.value) return;
        globalFilter.value = value;
        pagination.value.pageIndex = 0; // Reset to first page
        scheduleRefetch();
    };

    function buildFilterParams(filters: ColumnFiltersState) {
        const obj: Record<string, any> = {};
        for (const f of filters) {
            obj[f.id] = f.value;
        }
        return obj;
    }

    const dataTableRef = ref<any>(null);
    let searchDebounce: ReturnType<typeof setTimeout> | null = null;

    const scheduleRefetch = () => {
        if (searchDebounce) clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => {
            pagination.value.pageIndex = 0;
            refetch();
        }, 300);
    };

    watch(
        () => dataTableRef.value?.table?.getState().columnFilters,
        (filters) => {
            if (!initialQueryDone.value) return;
            if (!filters) return;
            columnFilters.value = filters as ColumnFiltersState;
            scheduleRefetch();
        },
        { deep: true },
    );

    watch(
        () => dataTableRef.value?.table?.getState().globalFilter,
        (value) => {
            if (!initialQueryDone.value) return;
            if (typeof value !== 'string') return;
            globalFilter.value = value;
            scheduleRefetch();
        },
    );

    watch(
        () => dataTableRef.value?.table?.getState().sorting,
        (value) => {
            if (!initialQueryDone.value) return;
            sorting.value = (value || []) as SortingState;
            scheduleRefetch();
        },
        { deep: true },
    );

    onMounted(() => {
        initialQueryDone.value = true;
    });
</script>

<template>
    <Head :title="$t('profile.profile_settings')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard v-if="hasPermission(Permission.USERS_VIEW)">
            <DataTable
                ref="dataTableRef"
                :table-key="QueryKey.USERS"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :manual-pagination="true"
                :manual-sorting="true"
                :manual-filtering="true"
                :total-records="totalRecords"
                :default-pagination="pagination"
                :auto-url-search="true"
                :default-column-visibility="{
                    id: false,
                }"
                @global-filter-change="handleGlobalFilterChange"
                :default-sorting="[
                    {
                        id: 'name',
                        desc: false,
                    },
                ]"
                @pagination-change="handlePaginationChange"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('users.page_header') }}
                    </CardTitle>
                </template>
                <template #actions>
                    <DataTableToolbarActions name="users">
                        <CreateUser v-if="hasPermission(Permission.USERS_CREATE)" />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
